import { wait } from './util';

type HeaderMap = {
  [key: string]: string | undefined;
};

type Method = 'GET' | 'POST' | 'PATCH' | 'DELETE';

export const StandardHeaders = {
  OCP: 'Ocp-Apim-Subscription-Key',
  AUTH: 'Authorization',
} as const;
export type StandardHeaders = (typeof StandardHeaders)[keyof typeof StandardHeaders];

export function buildStandardHeaders(ocp?: string, auth?: string): HeaderMap {
  const map: HeaderMap = {};
  if (ocp) {
    map[StandardHeaders.OCP] = ocp;
  }
  if (auth) {
    map[StandardHeaders.AUTH] = `Bearer ${auth}`;
  }
  return map;
}

function buildRequestOptions(
  method: Method,
  headers?: HeaderMap,
  body?: Record<string, unknown>,
  timeout = 10000 //10s
): RequestInit {
  const h = new Headers();
  if (typeof headers === 'object') {
    for (const [key, value] of Object.entries(headers)) {
      if (value) {
        h.set(key, value);
      }
    }
  }
  const abort = new AbortController();
  if (timeout > 0) {
    setTimeout(() => abort.abort(), timeout);
  }

  const options: RequestInit = {
    method,
    headers: h,
    signal: abort.signal,
  };

  if (body !== null) {
    options.body = JSON.stringify(body);
    h.set('Content-Type', 'application/json');
  }

  return options;
}

export function request(
  url: string,
  method: Method,
  headers?: HeaderMap,
  body?: Record<string, unknown>,
  retryLimit = 0,
  timeout?: number
): Promise<Response> | undefined {
  const options = buildRequestOptions(method, headers, body, timeout);
  return backoffRetry(() => fetch(url, options), retryLimit, 100);
}

export async function backoffRetry<T>(
  fn: () => Promise<T>,
  attempts = 2,
  backoff = 500
): Promise<T> {
  let attempt = 0;
  while (attempt++ <= attempts) {
    try {
      return await fn();
    } catch (e) {
      if (attempt > attempts) {
        throw e;
      }
      await wait(backoff);
    }
  }
  throw new Error('Unreachable');
}
